import { useCallback, useEffect, useState } from 'react';
import { useApolloClient, useReactiveVar } from '@apollo/client';

import {
  FakturowniaInvoiceIdsDocument,
  FakturowniaInvoiceIdsQuery,
  FakturowniaInvoiceIdsQueryVariables,
} from 'core/graphql';

import { financeListsVersion } from './evictFinanceLists';

const BATCH_SIZE = 500;

interface IInvoiceCountHook {
  count: number;
  loading: boolean;
  refetch: () => void;
}

/**
 * Count of invoices matching the filter — walks ids-only pages (~95% lighter
 * than the full-row drain the dashboard KPI used before; spec 0008 round 2).
 * The proper long-term fix is a `needsLinkOnly` arg on
 * `fakturowniaInvoiceAggregates` (backend follow-up); this stays a pure
 * frontend swap until then.
 */
export const useFakturowniaInvoiceCount = (variables: {
  direction?: string;
  needsLinkOnly?: boolean;
}): IInvoiceCountHook => {
  const client = useApolloClient();
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refetchTick, setRefetchTick] = useState(0);

  const { direction, needsLinkOnly } = variables;

  // Recount when a money mutation / sync evicts the invoice lists so the
  // KPI converges with the register (finance-block audit).
  const listsVersion = useReactiveVar(financeListsVersion);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const countAll = async () => {
      let total = 0;
      let page = 1;
      try {
        // Sequential by design — a next page exists only if the current one
        // came back full.
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const result = await client.query<
            FakturowniaInvoiceIdsQuery,
            FakturowniaInvoiceIdsQueryVariables
          >({
            query: FakturowniaInvoiceIdsDocument,
            variables: {
              direction: direction || undefined,
              needsLinkOnly: needsLinkOnly || undefined,
              page,
              perPage: BATCH_SIZE,
            },
            fetchPolicy: 'network-only',
          });
          if (cancelled) return;
          const batch = result.data?.fakturowniaInvoices || [];
          total += batch.length;
          if (batch.length < BATCH_SIZE) break;
          page += 1;
        }
        if (!cancelled) setCount(total);
      } catch {
        // Non-critical KPI — keep the previous count on failure.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    countAll();
    return () => {
      cancelled = true;
    };
  }, [client, direction, needsLinkOnly, refetchTick, listsVersion]);

  const refetch = useCallback(() => setRefetchTick((t) => t + 1), []);

  return { count, loading, refetch };
};
